import { getGroupDir } from "./getGroupDir.js";
import { parsePath } from "./parsePath.js";
import {
  getOpfsRootDirName,
  getStorageStatus,
  invalidateStorageRoot,
  isStaleHandleError,
} from "./storage.js";
import { writeFileHandle, writeOpfsPathViaWorker } from "./writeFileHandle.js";
import type { ShadowClawDatabase } from "../db/types.js";

/**
 * Upload a file to a group's workspace.
 */
export async function uploadGroupFile(
  db: ShadowClawDatabase,
  groupId: string,
  filePath: string,
  blob: Blob,
): Promise<void> {
  const { dirs, filename } = parsePath(filePath);

  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const groupDir = await getGroupDir(db, groupId);
      let dir = groupDir;
      for (const seg of dirs) {
        dir = await dir.getDirectoryHandle(seg, { create: true });
      }

      const fileHandle = await dir.getFileHandle(filename, { create: true });

      try {
        await writeFileHandle(fileHandle, blob);

        return;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        const needsOpfsWorkerFallback =
          message.includes("Writable file streams are not supported") &&
          (await getStorageStatus(db)).type === "opfs";

        if (!needsOpfsWorkerFallback) {
          throw err;
        }
      }

      const safeId = groupId.replace(/:/g, "-");
      await writeOpfsPathViaWorker(
        [getOpfsRootDirName(), "groups", safeId, ...dirs, filename],
        blob,
      );

      return;
    } catch (err) {
      if (attempt === 0 && isStaleHandleError(err)) {
        invalidateStorageRoot();

        continue;
      }

      throw err;
    }
  }
}
